import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import {
  type PaymentTimelineItem,
  useDashboardScheduledUpcomingOverview,
} from '~entities/payment/api/dashboard-scheduled-upcoming-overview';

import { useGetCurrency } from '~shared/api/currency-api';
import D3YearlyHeatmap from '~shared/components/d3-yearly-heatmap';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export const ScheduledAndUpcoming = () => {
  const { data, isLoading } = useDashboardScheduledUpcomingOverview();
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-semibold'>Scheduled & Upcoming</h2>
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Expected Revenue (Next 7 Days)</CardTitle>
            <CardDescription>
              Projected income for the upcoming week
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold'>
              {isLoading ? (
                <div className='flex items-center justify-center'>
                  <Loader2 className='size-6 animate-spin' />
                </div>
              ) : (
                `${currencySymbol}${(data?.expectedRevenueNext7Days ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Expected Revenue (Next 30 Days)</CardTitle>
            <CardDescription>
              Projected income for the upcoming month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold'>
              {isLoading ? (
                <div className='flex items-center justify-center'>
                  <Loader2 className='size-6 animate-spin' />
                </div>
              ) : (
                `${currencySymbol}${(data?.expectedRevenueNext30Days ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className='pb-0'>
          <div className='flex min-w-0 items-center justify-between'>
            <CardTitle>Payment Timeline</CardTitle>
          </div>
        </CardHeader>
        <CardContent className='min-w-0'>
          <div className='min-w-0 overflow-x-auto pb-2'>
            <div className='min-w-[800px]'>
              <PaymentTimeline
                paymentTimeline={data?.paymentTimeline ?? []}
                isLoading={isLoading}
                currencySymbol={currencySymbol}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function PaymentTimeline({
  paymentTimeline,
  isLoading,
  currencySymbol,
}: {
  paymentTimeline: Array<PaymentTimelineItem>;
  isLoading: boolean;
  currencySymbol: string;
}) {
  const today = new Date();
  const year = today.getFullYear();
  const startMonth = today.getMonth();
  const numMonths = 6;

  // Build a map for quick lookup
  const paymentMap = useMemo(() => {
    const map: Record<string, { count: number; amount: number }> = {};
    for (const item of paymentTimeline) {
      map[item.date] = {
        count: item.paymentCount,
        amount: item.totalAmount,
      };
    }
    return map;
  }, [paymentTimeline]);

  // Prepare data for D3YearlyHeatmap
  const values = useMemo(() => {
    const startDate = new Date(year, startMonth, 1);
    const endMonth = Math.min(startMonth + numMonths - 1, 11);
    const endDate = new Date(year, endMonth + 1, 0);
    const arr: Array<{ date: string; count: number; amount: number }> = [];
    const d = new Date(startDate);
    while (d <= endDate) {
      const iso = format(d, 'yyyy-MM-dd');
      const payment = paymentMap[iso];
      arr.push({
        date: iso,
        count: payment?.count ?? 0,
        amount: payment?.amount ?? 0,
      });
      d.setDate(d.getDate() + 1);
    }
    return arr;
  }, [year, startMonth, numMonths, paymentMap]);

  // Find max count for color scale
  const maxCount = Math.max(1, ...values.map((v) => v.count));

  // Blue color scale
  const getColor = (count: number) => {
    if (!count) return '#fff';
    if (count >= maxCount) return '#1565c0';
    if (count >= maxCount * 0.85) return '#1976d2';
    if (count >= maxCount * 0.7) return '#1e88e5';
    if (count >= maxCount * 0.55) return '#42a5f5';
    if (count >= maxCount * 0.4) return '#64b5f6';
    if (count >= maxCount * 0.25) return '#90caf9';
    return '#e3f2fd';
  };

  if (isLoading) {
    return <div className='py-8 text-center'>Loading...</div>;
  }
  if (!paymentTimeline.length) {
    return (
      <div className='py-8 text-center text-muted-foreground'>
        No data available
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col items-center'>
      <div className='w-full'>
        <D3YearlyHeatmap
          year={year}
          data={values}
          startMonth={startMonth}
          numMonths={numMonths}
          cellSize={22}
          currencySymbol={currencySymbol}
          highlightBlock={(d) => d.date === format(today, 'yyyy-MM-dd')}
          disableBlock={(d) => d.date < format(today, 'yyyy-MM-dd')}
        />
      </div>
      <div className='ml-10 mt-4 flex items-center gap-2'>
        <span className='text-xs text-muted-foreground'>Payments</span>
        <div className='flex items-center gap-1'>
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: 8,
                background: getColor(Math.round(maxCount * p)),
                border: '1px solid #d1d5db',
              }}
            />
          ))}
        </div>
        <span className='text-xs text-muted-foreground'>{maxCount}+</span>
      </div>
    </div>
  );
}
